/**
 * Viewport renderer for the Inclined Plane simulation
 */

import type { SimulationState, Renderer } from '../types';
import { CONSTANTS } from '../simulation/physics';

export class ViewportRenderer implements Renderer {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private width: number;
  private height: number;

  // Coordinate conversion constants
  private readonly PADDING = 60;
  private readonly AXIS_OFFSET = 40; // space for projection axes

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Could not get 2D context');
    this.ctx = ctx;

    this.width = canvas.width;
    this.height = canvas.height;

    // Set up high DPI rendering
    this.setupCanvas();
  }

  private setupCanvas(): void {
    const dpr = window.devicePixelRatio || 1;
    const rect = this.canvas.getBoundingClientRect();

    this.canvas.width = rect.width * dpr;
    this.canvas.height = rect.height * dpr;

    this.width = this.canvas.width;
    this.height = this.canvas.height;

    this.ctx.scale(dpr, dpr);
  }

  /**
   * Calculate dynamic spacing between triangles based on triangle height
   */
  private getTriangleSpacing(triangleHeight: number): number {
    // Spacing should be proportional to triangle height
    // Use 35% of triangle height, with min/max bounds
    // Lower minimum (40px) for narrow viewports
    return Math.max(40, Math.min(200, triangleHeight * 0.35));
  }

  /**
   * Calculate the required canvas height based on plane angle
   */
  private calculateRequiredHeight(planeAngleDegrees: number, canvasWidth: number): number {
    const planeAngleRad = planeAngleDegrees * (Math.PI / 180);

    // Calculate triangle height based on plane length and angle
    const availableWidth = canvasWidth - 2 * this.PADDING - this.AXIS_OFFSET;
    const pixelsPerMeter = availableWidth / (CONSTANTS.PLANE_LENGTH * Math.cos(planeAngleRad) * 1.1);
    const triangleHeight = CONSTANTS.PLANE_LENGTH * Math.sin(planeAngleRad) * pixelsPerMeter;

    // Calculate spacing between triangles
    const spacing = this.getTriangleSpacing(triangleHeight);

    // Total height: top padding + first triangle + spacing + second triangle + bottom padding
    const totalHeight = 2 * this.PADDING + 2 * triangleHeight + spacing;

    return totalHeight;
  }

  resize(planeAngleDegrees: number): void {
    // Get the parent element (viewport-panel) to set its height
    const parentElement = this.canvas.parentElement;
    if (!parentElement) return;

    const rect = parentElement.getBoundingClientRect();
    const calculatedHeight = this.calculateRequiredHeight(planeAngleDegrees, rect.width);

    // Clamp height to reasonable bounds
    // Use calculated height with safety bounds: min 200px, max 800px
    const height = Math.max(200, Math.min(800, calculatedHeight));

    // Set the height on the parent container, not the canvas
    // The canvas will fill 100% of parent via CSS
    parentElement.style.height = `${height}px`;

    this.setupCanvas();
  }

  /**
   * Get triangle geometry for a given plane index
   * Right angle is at bottom-left, hypotenuse goes from topLeft to bottomRight
   * @param planeIndex - 0 for top triangle (blue), 1 for bottom triangle (red)
   * @param planeAngleDegrees - angle of the incline in degrees
   */
  private getTriangleGeometry(planeIndex: number, planeAngleDegrees: number) {
    const dpr = window.devicePixelRatio || 1;
    const canvasWidth = this.width / dpr;

    const planeAngleRad = planeAngleDegrees * (Math.PI / 180);

    // Calculate dimensions in pixels
    const availableWidth = canvasWidth - 2 * this.PADDING - this.AXIS_OFFSET;
    const pixelsPerMeter = availableWidth / (CONSTANTS.PLANE_LENGTH * Math.cos(planeAngleRad) * 1.1);

    const horizontalLength = CONSTANTS.PLANE_LENGTH * Math.cos(planeAngleRad) * pixelsPerMeter;
    const verticalHeight = CONSTANTS.PLANE_LENGTH * Math.sin(planeAngleRad) * pixelsPerMeter;

    // Calculate dynamic spacing between triangles
    const triangleSpacing = this.getTriangleSpacing(verticalHeight);

    // Bottom-left corner (right angle) position
    const bottomLeftX = this.PADDING + this.AXIS_OFFSET;
    const bottomLeftY = this.PADDING + planeIndex * (verticalHeight + triangleSpacing) + verticalHeight;

    return {
      bottomLeft: { x: bottomLeftX, y: bottomLeftY },  // right angle at bottom-left
      topLeft: { x: bottomLeftX, y: bottomLeftY - verticalHeight },  // top of vertical side
      bottomRight: { x: bottomLeftX + horizontalLength, y: bottomLeftY },  // end of horizontal side
      pixelsPerMeter,
      planeAngleRad
    };
  }

  /**
   * Get ball position on the hypotenuse with bottom touching (for flipped triangle)
   * @param position - meters along the plane from start
   * @param radius - ball radius in meters
   * @param triangleGeom - triangle geometry
   */
  private getBallCoordinates(
    position: number,
    radius: number,
    triangleGeom: ReturnType<typeof this.getTriangleGeometry>
  ): { x: number; y: number } {
    // Position along hypotenuse starting from topLeft going down to bottomRight
    // Ball rolls from topLeft down the hypotenuse
    const distancePixels = position * triangleGeom.pixelsPerMeter;
    const baseX = triangleGeom.topLeft.x + distancePixels * Math.cos(triangleGeom.planeAngleRad);
    const baseY = triangleGeom.topLeft.y + distancePixels * Math.sin(triangleGeom.planeAngleRad);

    // Offset perpendicular to ramp so ball sits ON TOP of the ramp
    // Perpendicular to the slope: rotate 90° counterclockwise from slope direction
    // For a downward-right slope, perpendicular up-right is (-sin, -cos) in screen coords
    const radiusPixels = radius * triangleGeom.pixelsPerMeter;
    const offsetX = -radiusPixels * Math.sin(triangleGeom.planeAngleRad);
    const offsetY = -radiusPixels * Math.cos(triangleGeom.planeAngleRad);

    return {
      x: baseX + offsetX,
      y: baseY + offsetY
    };
  }

  /**
   * Draw a right triangle as the ramp
   */
  private drawTriangle(planeIndex: number, planeAngleDegrees: number, color: string): void {
    const ctx = this.ctx;
    const geom = this.getTriangleGeometry(planeIndex, planeAngleDegrees);

    // Draw triangle: bottomLeft (right angle) -> bottomRight -> topLeft -> back to bottomLeft
    ctx.fillStyle = color;
    ctx.strokeStyle = '#1a1a1a';
    ctx.lineWidth = 3;

    ctx.beginPath();
    ctx.moveTo(geom.bottomLeft.x, geom.bottomLeft.y);
    ctx.lineTo(geom.bottomRight.x, geom.bottomRight.y);
    ctx.lineTo(geom.topLeft.x, geom.topLeft.y);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
  }

  /**
   * Draw trail markers for a ball on the hypotenuse and vertical projection axis
   */
  private drawTrail(
    trail: Array<{ position: number; time: number }>,
    color: string,
    planeIndex: number,
    planeAngleDegrees: number,
    ballRadius: number
  ): void {
    const ctx = this.ctx;

    if (trail.length === 0) return;

    const geom = this.getTriangleGeometry(planeIndex, planeAngleDegrees);

    // Draw markers on the hypotenuse
    ctx.fillStyle = color;

    for (const marker of trail) {
      const ballPos = this.getBallCoordinates(marker.position, ballRadius, geom);

      // Draw marker on hypotenuse
      ctx.beginPath();
      ctx.arc(ballPos.x, ballPos.y, 4, 0, 2 * Math.PI);
      ctx.fill();

      // Draw vertical projection only
      // The projection should be at the vertical position of the ball's CENTER, not the contact point
      // We need to account for the ball's radius offset perpendicular to the ramp
      const radiusPixels = ballRadius * geom.pixelsPerMeter;
      const offsetY = -radiusPixels * Math.cos(geom.planeAngleRad);
      const verticalY = geom.topLeft.y + marker.position * Math.sin(geom.planeAngleRad) * geom.pixelsPerMeter + offsetY;

      ctx.fillStyle = color + 'AA'; // semi-transparent
      ctx.beginPath();
      ctx.arc(geom.bottomLeft.x - 10, verticalY, 3, 0, 2 * Math.PI);
      ctx.fill();

      // Reset fill style for next hypotenuse marker
      ctx.fillStyle = color;
    }
  }

  /**
   * Draw a ball on the hypotenuse with correct positioning
   */
  private drawBall(
    position: number,
    radius: number,
    color: string,
    planeIndex: number,
    planeAngleDegrees: number
  ): void {
    const ctx = this.ctx;
    const geom = this.getTriangleGeometry(planeIndex, planeAngleDegrees);
    const point = this.getBallCoordinates(position, radius, geom);

    // Convert radius from meters to pixels
    const radiusPixels = Math.max(8, radius * geom.pixelsPerMeter);

    // Draw ball shadow
    ctx.fillStyle = 'rgba(0, 0, 0, 0.2)';
    ctx.beginPath();
    ctx.arc(point.x + 2, point.y + 2, radiusPixels, 0, 2 * Math.PI);
    ctx.fill();

    // Draw ball with gradient for 3D effect
    const gradient = ctx.createRadialGradient(
      point.x - radiusPixels / 3,
      point.y - radiusPixels / 3,
      0,
      point.x,
      point.y,
      radiusPixels
    );
    gradient.addColorStop(0, this.lightenColor(color));
    gradient.addColorStop(1, color);

    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(point.x, point.y, radiusPixels, 0, 2 * Math.PI);
    ctx.fill();

    // Draw ball outline
    ctx.strokeStyle = this.darkenColor(color);
    ctx.lineWidth = 2;
    ctx.stroke();
  }

  /**
   * Lighten a color
   */
  private lightenColor(color: string): string {
    // Simple color lightening for hex colors
    if (color === '#3b82f6') return '#93c5fd'; // blue
    if (color === '#ef4444') return '#fca5a5'; // red
    return color;
  }

  /**
   * Darken a color
   */
  private darkenColor(color: string): string {
    if (color === '#3b82f6') return '#1e40af'; // blue
    if (color === '#ef4444') return '#991b1b'; // red
    return color;
  }

  /**
   * Draw scale indicator in upper right corner
   */
  private drawScale(planeAngleDegrees: number): void {
    const ctx = this.ctx;
    const dpr = window.devicePixelRatio || 1;
    const canvasWidth = this.width / dpr;

    // Get pixels per meter from triangle geometry
    const geom = this.getTriangleGeometry(0, planeAngleDegrees);
    const scaleLength = 5; // 5 meters
    const scaleLengthPixels = scaleLength * geom.pixelsPerMeter;

    // Position in upper right corner
    const padding = 20;
    const x = canvasWidth - padding - scaleLengthPixels;
    const y = 30;

    // Draw scale line
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x + scaleLengthPixels, y);
    ctx.stroke();

    // Draw tick marks at ends
    ctx.beginPath();
    ctx.moveTo(x, y - 5);
    ctx.lineTo(x, y + 5);
    ctx.moveTo(x + scaleLengthPixels, y - 5);
    ctx.lineTo(x + scaleLengthPixels, y + 5);
    ctx.stroke();

    // Draw scale label
    ctx.fillStyle = '#000';
    ctx.font = 'bold 14px Inter, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'bottom';
    ctx.fillText(`${scaleLength} m`, x + scaleLengthPixels / 2, y - 8);
  }

  /**
   * Render the complete scene
   */
  render(state: SimulationState, planeAngleDegrees: number): void {
    const ctx = this.ctx;
    const dpr = window.devicePixelRatio || 1;
    const canvasWidth = this.width / dpr;
    const canvasHeight = this.height / dpr;

    // Clear canvas with grayish background
    ctx.fillStyle = '#dadada';
    ctx.fillRect(0, 0, canvasWidth, canvasHeight);

    // Draw both right triangles (stacked vertically)
    // Blue ball triangle (top)
    this.drawTriangle(0, planeAngleDegrees, '#b8d4f1');
    // Red ball triangle (bottom)
    this.drawTriangle(1, planeAngleDegrees, '#f1c0c0');

    // Draw trails with projections
    this.drawTrail(state.blueTrail, state.blueBall.color, 0, planeAngleDegrees, state.blueBall.radius);
    this.drawTrail(state.redTrail, state.redBall.color, 1, planeAngleDegrees, state.redBall.radius);

    // Draw balls on their respective triangles
    if (!state.blueBall.hasLanded || state.blueBall.position < CONSTANTS.PLANE_LENGTH) {
      this.drawBall(
        state.blueBall.position,
        state.blueBall.radius,
        state.blueBall.color,
        0, // top triangle
        planeAngleDegrees
      );
    }

    if (!state.redBall.hasLanded || state.redBall.position < CONSTANTS.PLANE_LENGTH) {
      this.drawBall(
        state.redBall.position,
        state.redBall.radius,
        state.redBall.color,
        1, // bottom triangle
        planeAngleDegrees
      );
    }

    // Draw scale indicator
    this.drawScale(planeAngleDegrees);

    // Draw time display
    ctx.fillStyle = '#1a1a2e';
    ctx.font = 'bold 16px Inter, sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText(`Time: ${state.time.toFixed(2)}s`, 20, 25);
  }
}
